'use client';

/**
 * AuthContext - Wrapper for authentication state
 *
 * This module re-exports authentication hooks from AppShellContext
 * for consistent use throughout the application.
 *
 * Usage:
 *   import { useAuth } from '@/contexts/AuthContext';
 *   const { user, isLoading } = useAuth();
 */

import {
  useAuthState,
  useIsAuthenticated,
} from '@/components/shell/AppShellContext';
import type { AuthContext as AuthContextType } from '@/lib/routes/types';

/**
 * Hook to access authentication state
 *
 * @returns Authentication state with user and loading status
 *
 * @example
 * const { user, isLoading } = useAuth();
 * if (isLoading) return <Spinner />;
 * if (!user) return <LoginPrompt />;
 */
export function useAuth(): {
  user: AuthContextType['user'];
  isLoading: boolean;
  isAuthenticated: boolean;
} {
  const { user, isAuthLoading } = useAuthState();
  const isAuthenticated = useIsAuthenticated();

  return {
    user,
    isLoading: isAuthLoading,
    isAuthenticated,
  };
}

